/**
 * The ending (spec 02 §9.4, research R§7 "Ending"): a starfield in place of the stage, the big boss
 * walking back and forth along the bottom while the ending tune plays; the lines are the session's
 * banners. Stars are self-lit specks at three depths, twinkling.
 */

import { Group, type Material, Mesh } from 'three';
import { buildBoss } from '../actors/boss';
import type { Puppet } from '../actors/puppet';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { poseBoss } from './bossView';

const STARS = 140;
/** Half the width the boss paces, world units. */
const PACE = 9;

export class EndingView {
  readonly group = new Group();
  private readonly layers: Mesh[] = [];
  private readonly boss: Puppet;

  constructor(p: Palette, glow: Material, bossMaterial: Material) {
    // A fixed scatter (a small LCG: the same sky every time).
    let s = 0x2468ace;
    const rand = (): number => {
      s = (s * 1103515245 + 12345) >>> 0;
      return s / 0x100000000;
    };
    const colours = [p.color('paper.0'), p.color('bob.3'), p.color('bubTrim.2')];
    for (let layer = 0; layer < 3; layer++) {
      const mb = new MeshBuilder();
      for (let i = 0; i < STARS / 3; i++) {
        const size = 0.06 + rand() * 0.1 + layer * 0.03;
        const c = colours[Math.floor(rand() * colours.length)] ?? colours[0];
        if (c) mb.box(size, size, size, c, place(rand() * 32 - 16, rand() * 25 - 12.5, -3 + layer));
      }
      const mesh = new Mesh(mb.build(), glow);
      this.layers.push(mesh);
      this.group.add(mesh);
    }
    this.boss = buildBoss(p, bossMaterial);
    this.group.add(this.boss.root);
  }

  /** Free the stars and the boss (the materials are the caller's). */
  dispose(): void {
    for (const m of this.layers) m.geometry.dispose();
    this.boss.dispose();
  }

  update(t: number): void {
    this.layers.forEach((m, i) => {
      m.visible = Math.sin(t * (2 + i) + i * 2.1) > -0.7;
      m.position.x = Math.sin(t * 0.05 * (i + 1)) * 0.5;
    });
    // Back and forth along the floor, turning at each end.
    const x = Math.sin(t * 0.35) * PACE;
    const dir = Math.cos(t * 0.35) >= 0 ? 1 : -1;
    this.boss.root.position.set(x, -12.5 + 1, 0);
    this.boss.turnTo(dir, t);
    poseBoss(this.boss, t, 1.4);
  }
}
